import { useEffect, useId, useRef, useState, type ButtonHTMLAttributes, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { Moon, SlidersHorizontal, Sun, SunMoon, Type } from 'lucide-react';
import { cx } from '@/ui';
import { LANGS, setLang, useLang, useT } from '@/i18n';
import { setLarge, setTheme, useTheme, type Theme } from '@/theme';

/**
 * Sprache & Darstellung — **ein** Knopf statt sieben (docs/notes/darstellung.md).
 *
 * Vorher standen überall DE · EN · IT, automatisch · hell · dunkel und „große
 * Schrift" als sieben Knöpfe nebeneinander (Betreiber, 09/2026: „viel zu
 * präsent für etwas, das man einmal einstellt"). Jetzt ein kleiner Knopf mit
 * dem Sprachkürzel, der ein Menü mit drei Zeilen öffnet: Sprache, Darstellung,
 * Schrift. Eine Wahl wirkt sofort (setLang / setTheme / setLarge), das Menü
 * bleibt offen — wer Sprache *und* hell umstellen will, muss nicht zweimal
 * öffnen.
 *
 * Tastatur nach dem Menü-Muster der WAI-ARIA-Praxis: `aria-haspopup="menu"`,
 * `role="menu"`, Einträge `menuitemradio` mit `aria-checked`; Pfeiltasten
 * wandern durch alle Einträge, Home/Ende an den Rand, Escape schließt und gibt
 * den Fokus an den Knopf zurück, Tab ebenso; ein Klick daneben schließt.
 *
 * `inline`: die drei Zeilen ohne Knopf und Menü, etwa im Blatt „Mehr" am
 * Handy, das ohnehin ein Dialog ist — ein Menü in einem Dialog wäre zu viel.
 * `oben`: das Menü klappt nach oben auf (Fuß der Seitenleiste).
 */
export function Darstellung({ className, inline, oben, links, beschriftet }: { className?: string; inline?: boolean; oben?: boolean; links?: boolean; beschriftet?: boolean }) {
  const t = useT();
  const lang = useLang();
  const [offen, setOffen] = useState(false);
  const wurzel = useRef<HTMLDivElement>(null);
  const knopf = useRef<HTMLButtonElement>(null);
  const menue = useRef<HTMLDivElement>(null);
  const menueId = useId();
  const titel = t('Sprache & Darstellung');

  // Klick daneben schließt — ohne den Fokus zu verschieben: Wer daneben klickt, will dorthin
  useEffect(() => {
    if (!offen) return;
    const onDown = (e: PointerEvent) => {
      if (!wurzel.current?.contains(e.target as Node)) setOffen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [offen]);

  // Beim Öffnen wandert der Fokus in das Menü — auf die gewählte Sprache
  useEffect(() => {
    if (!offen) return;
    const el = menue.current;
    if (!el) return;
    const ziel = el.querySelector<HTMLElement>('[role="menuitemradio"][aria-checked="true"]') ?? el.querySelector<HTMLElement>('[role="menuitemradio"]');
    (ziel ?? el).focus();
  }, [offen]);

  const schliessen = (fokusZurueck: boolean) => {
    setOffen(false);
    if (fokusZurueck) knopf.current?.focus();
  };

  const onKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      schliessen(true);
      return;
    }
    if (e.key === 'Tab') {
      // Tab verlässt ein Menü — zurück zum Knopf, von dort geht es weiter
      e.preventDefault();
      schliessen(true);
      return;
    }
    const eintraege = [...(menue.current?.querySelectorAll<HTMLElement>('[role="menuitemradio"]') ?? [])];
    if (eintraege.length === 0) return;
    const i = eintraege.indexOf(document.activeElement as HTMLElement);
    let ziel: number | null = null;
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') ziel = (i + 1) % eintraege.length;
    else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') ziel = (i - 1 + eintraege.length) % eintraege.length;
    else if (e.key === 'Home') ziel = 0;
    else if (e.key === 'End') ziel = eintraege.length - 1;
    if (ziel === null) return;
    e.preventDefault();
    eintraege[ziel]?.focus();
  };

  if (inline) {
    return (
      <div className={cx('grid gap-3', className)} data-darstellung="inline" aria-label={titel} role="group">
        <Gruppen t={t} />
      </div>
    );
  }

  const kuerzel = LANGS.find((l) => l.key === lang)?.label ?? lang.toUpperCase();
  return (
    <div ref={wurzel} className={cx('relative inline-flex', className)}>
      {/* Wie die anderen Knöpfe in den Kopfzeilen: klein und ruhig am Laptop, 44 px am Handy */}
      <button
        ref={knopf}
        type="button"
        data-darstellung="knopf"
        className={cx(
          'inline-flex items-center gap-1.5 border border-transparent cursor-pointer select-none text-muted hover:text-ink hover:bg-panel2 transition-colors',
          'px-2 py-1 max-md:min-h-11 max-md:min-w-11 max-md:px-2.5',
          beschriftet ? 'w-full justify-start' : 'justify-center',
          offen && 'bg-panel2 text-ink',
        )}
        onClick={() => setOffen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={offen}
        aria-controls={offen ? menueId : undefined}
        aria-label={titel}
        title={titel}
      >
        <SlidersHorizontal size={14} className="shrink-0" aria-hidden="true" />
        {beschriftet && <span className="text-xs font-bold tracking-tight truncate">{titel}</span>}
        <span className={cx('label text-ink', beschriftet && 'ml-auto')}>{kuerzel}</span>
      </button>
      {offen && (
        <div
          ref={menue}
          id={menueId}
          role="menu"
          aria-label={titel}
          tabIndex={-1}
          onKeyDown={onKey}
          className={cx(
            'absolute z-50 grid gap-3 p-3 bg-panel border border-line shadow-[0_20px_50px_-30px_#000] outline-none',
            'w-72 max-w-[calc(100vw-2rem)]',
            oben ? 'bottom-full mb-1' : 'top-full mt-1',
            links ? 'left-0' : 'right-0',
          )}
        >
          <Gruppen t={t} />
        </div>
      )}
    </div>
  );
}

/** Die drei Zeilen: Sprache, Darstellung, Schrift — jede eine Reihe abgesetzter Wahlen */
function Gruppen({ t }: { t: ReturnType<typeof useT> }) {
  const lang = useLang();
  const { theme, large } = useTheme();
  const themen: { key: Theme; label: string; titel: string; icon: typeof Sun }[] = [
    { key: 'auto', label: t('Automatisch'), titel: t('Automatisch (wie das Gerät)'), icon: SunMoon },
    { key: 'light', label: t('Hell'), titel: t('Helle Darstellung (draußen)'), icon: Sun },
    { key: 'dark', label: t('Dunkel'), titel: t('Dunkle Darstellung'), icon: Moon },
  ];
  return (
    <>
      <Gruppe label={t('Sprache')}>
        {LANGS.map((l) => (
          <Wahl key={l.key} checked={lang === l.key} onClick={() => setLang(l.key)} data-sprache={l.key} lang={l.key}>
            {l.name}
          </Wahl>
        ))}
      </Gruppe>
      <Gruppe label={t('Darstellung')}>
        {themen.map(({ key, label, titel, icon: Icon }) => (
          <Wahl key={key} checked={theme === key} onClick={() => setTheme(key)} title={titel} data-theme-wahl={key}>
            <Icon size={13} className="shrink-0" aria-hidden="true" /> {label}
          </Wahl>
        ))}
      </Gruppe>
      <Gruppe label={t('Schrift')}>
        <Wahl checked={!large} onClick={() => setLarge(false)} data-schrift="normal">
          {t('Normal|Schrift')}
        </Wahl>
        <Wahl checked={large} onClick={() => setLarge(true)} title={t('Große Schrift')} data-schrift="large">
          <Type size={13} className="shrink-0" aria-hidden="true" /> {t('Groß')}
        </Wahl>
      </Gruppe>
    </>
  );
}

function Gruppe({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div role="group" aria-label={label} className="grid gap-1.5">
      <span className="label">{label}</span>
      <div className="flex border border-line">{children}</div>
    </div>
  );
}

function Wahl({ checked, children, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { checked: boolean }) {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={checked}
      tabIndex={-1}
      className={cx(
        'flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs cursor-pointer whitespace-nowrap border-l border-line first:border-l-0 min-h-9 max-md:min-h-11 outline-none focus-visible:ring-2 focus-visible:ring-blue focus-visible:ring-inset',
        checked ? 'bg-panel2 text-ink font-bold' : 'text-muted hover:text-ink',
      )}
      {...rest}
    >
      {children}
    </button>
  );
}
